import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaPlus, FaMinus, FaShoppingCart } from 'react-icons/fa';
import { getCart, removeFromCart, updateCartQuantity, clearCart, createCheckout } from '../services/api';
import './Cart.css';

function Cart() {
  const [cart, setCart] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadCart();

    const handleCartUpdate = () => {
      loadCart();
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    return () => window.removeEventListener('cartUpdated', handleCartUpdate);
  }, []);

  const loadCart = () => {
    const cartData = getCart();
    setCart(cartData);
  };

  const handleRemove = (productId, variantId) => {
    removeFromCart(productId, variantId);
  };

  const handleQuantityChange = (productId, variantId, newQuantity) => {
    if (newQuantity < 1) return;
    updateCartQuantity(productId, variantId, newQuantity);
  };

  const calculateTotal = () => {
    return cart.reduce((total, item) => {
      const price = item.product.price || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return;

    setLoading(true);
    try {
      const checkoutData = {
        cart: cart.map(item => ({
          productId: item.productId,
          variantId: item.variantId,
          quantity: item.quantity
        }))
      };

      const response = await createCheckout(checkoutData);
      
      if (response.data?.url) {
        // Clear cart and redirect to checkout
        clearCart();
        window.location.href = response.data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to create checkout. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (cart.length === 0) {
    return (
      <div className="cart-page">
        <div className="container">
          <div className="cart-empty">
            <FaShoppingCart className="empty-icon" />
            <h2>Your Cart is Empty</h2>
            <p>Add some products to get started</p>
            <button className="btn-primary" onClick={() => navigate('/products')}>
              Browse Products
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="container">
        <div className="cart-header">
          <h1>Shopping Cart</h1>
          <button className="btn-secondary" onClick={() => clearCart()}>
            Clear Cart
          </button>
        </div>

        <div className="cart-content">
          <div className="cart-items">
            {cart.map(item => (
              <div key={`${item.productId}-${item.variantId}`} className="cart-item">
                <div className="cart-item-info">
                  <h3>{item.product.name}</h3>
                  {item.product.description && (
                    <p className="cart-item-description">{item.product.description}</p>
                  )}
                  <p className="cart-item-price">${item.product.price?.toFixed(2)}</p>
                </div>

                <div className="cart-item-actions">
                  <div className="quantity-controls">
                    <button 
                      onClick={() => handleQuantityChange(item.productId, item.variantId, item.quantity - 1)}
                      disabled={item.quantity <= 1}
                    >
                      <FaMinus />
                    </button>
                    <span className="quantity">{item.quantity}</span>
                    <button 
                      onClick={() => handleQuantityChange(item.productId, item.variantId, item.quantity + 1)}
                    >
                      <FaPlus />
                    </button>
                  </div>

                  <button 
                    className="remove-button"
                    onClick={() => handleRemove(item.productId, item.variantId)}
                  >
                    <FaTrash /> Remove
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="cart-summary">
            <h2>Order Summary</h2>
            <div className="summary-row">
              <span>Subtotal ({cart.reduce((sum, item) => sum + item.quantity, 0)} items)</span>
              <span>${calculateTotal().toFixed(2)}</span>
            </div>
            <div className="summary-row total">
              <span>Total</span>
              <span>${calculateTotal().toFixed(2)}</span>
            </div>
            <button 
              className="btn-primary checkout-button" 
              onClick={handleCheckout}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Proceed to Checkout'}
            </button>
            <button 
              className="btn-secondary continue-shopping"
              onClick={() => navigate('/products')}
            >
              Continue Shopping
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Cart;
